import React, { useState, useEffect } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Mic, Play, Volume2, Lock, X } from 'lucide-react';
import { usePodcastGenerator } from '@/hooks/usePodcastGenerator';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@/hooks/useTranslation';
import GoogleMapSearchBar, { LocationDetails } from '@/components/GoogleMapSearchBar';

interface MobilePodcastGeneratorProps {
  isOpen: boolean;
  onClose: () => void;
  onPodcastGenerated?: (podcast: any) => void;
  initialSearchText?: string;
  preselectedLanguage?: 'fr' | 'en' | null;
}

const MobilePodcastGenerator: React.FC<MobilePodcastGeneratorProps> = ({
  isOpen,
  onClose,
  onPodcastGenerated,
  initialSearchText = '',
  preselectedLanguage = null
}) => {
  const [selectedLocation, setSelectedLocation] = useState<LocationDetails | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<'fr' | 'en'>('fr');
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const { t, language } = useTranslation();
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const {
    isGenerating,
    generatedPodcast,
    error,
    availableLanguages,
    isLocationBlocked,
    generatePodcast,
    checkExistingPodcasts,
    reset
  } = usePodcastGenerator();

  const handleLocationSelect = async (location: LocationDetails) => {
    console.log('🎯 Location selected in MobilePodcastGenerator:', location);
    setSelectedLocation(location);
    await checkExistingPodcasts(location);
    
    if (!availableLanguages.includes(selectedLanguage) && availableLanguages.length > 0) {
      setSelectedLanguage(availableLanguages[0]);
    }
  };

  useEffect(() => {
    if (preselectedLanguage && availableLanguages.includes(preselectedLanguage)) {
      setSelectedLanguage(preselectedLanguage);
    }
  }, [preselectedLanguage, availableLanguages]);

  // Keep the drawer glued just above the mobile keyboard using VisualViewport
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const setVvh = () => {
      const height = vv.height;
      document.documentElement.style.setProperty('--vvh', `${height}px`);
      const keyboardHeight = Math.max(0, window.innerHeight - height - (vv.offsetTop || 0));
      setIsKeyboardOpen(keyboardHeight > 60);
    };
    setVvh();
    vv.addEventListener('resize', setVvh);
    vv.addEventListener('scroll', setVvh);
    return () => {
      vv.removeEventListener('resize', setVvh);
      vv.removeEventListener('scroll', setVvh);
    };
  }, []);

  const handleGenerate = async () => {
    if (!isAuthenticated) {
      toast({
        title: t('authRequired'),
        description: t('authRequiredDesc'),
        variant: "destructive",
      });
      onClose();
      window.location.href = '/auth';
      return;
    }

    if (!selectedLocation || isLocationBlocked) {
      console.warn('❌ Cannot generate podcast - location blocked or not selected');
      return;
    }

    try {
      console.log('🎙️ Starting mobile podcast generation for location:', {
        name: selectedLocation.name,
        city: selectedLocation.city,
        country: selectedLocation.country,
        coordinates: [selectedLocation.latitude, selectedLocation.longitude],
        language: selectedLanguage
      });
      
      const podcast = await generatePodcast(selectedLocation, selectedLanguage);
      console.log('✅ Mobile podcast generated successfully:', podcast);
      
      if (onPodcastGenerated) {
        onPodcastGenerated(podcast);
      }
    } catch (error) {
      console.error('❌ Error generating mobile podcast:', error);
    }
  };

  const handleClose = () => {
    console.log('🚪 Closing MobilePodcastGenerator drawer');
    reset();
    setSelectedLocation(null);
    setSelectedLanguage('fr');
    onClose();
  };

  const currentDescription = generatedPodcast ? selectedLanguage === 'fr' ? generatedPodcast.Description_FR : generatedPodcast.Description_EN : '';
  const currentAudioFile = generatedPodcast ? selectedLanguage === 'fr' ? generatedPodcast.AudioFile_FR : generatedPodcast.AudioFile_EN : null;

  const isLanguageDisabled = (lang: 'fr' | 'en') => {
    return !availableLanguages.includes(lang);
  };

  return (
    <Drawer open={isOpen} onOpenChange={handleClose}>
      <DrawerContent className="overflow-hidden" style={{ height: "calc(var(--vvh, 100dvh))", maxHeight: "calc(var(--vvh, 100dvh))" }}>
        <DrawerHeader className="pb-4">
          <div className="flex items-center justify-between">
            <DrawerTitle className="flex items-center gap-2 text-xl font-bold">
              <Mic className="w-6 h-6 text-primary" />
              {t('createPodcastTitle')}
            </DrawerTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DrawerHeader>

        <div className="px-4 pb-4 space-y-4 flex-1 overflow-y-auto">
          {/* Google Maps Search Bar with integrated map */}
          <div className="space-y-3">
            <GoogleMapSearchBar 
              onLocationSelect={handleLocationSelect} 
              selectedLocation={selectedLocation} 
              placeholder={t('generatorSearchPlaceholder')} 
              initialValue={initialSearchText} 
            />
          </div>

          {/* Location Status */}
          {selectedLocation && isLocationBlocked && (
            <Card className="p-4 bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-orange-600 flex-shrink-0" />
                <div>
                  <span className="text-sm font-medium text-orange-800 dark:text-orange-200 block">
                    {t('placeComplete')}
                  </span>
                  <p className="text-xs mt-1 text-orange-700 dark:text-orange-300">
                    {t('placeCompleteDesc')}
                  </p>
                </div>
              </div>
            </Card>
          )}

          {selectedLocation && !isLocationBlocked && availableLanguages.length < 2 && (
            <Card className="p-4 bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
              <div className="flex items-center gap-3">
                <Volume2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div>
                  <span className="text-sm font-medium text-blue-800 dark:text-blue-200 block">
                    {t('limitedLanguages')}
                  </span>
                  <p className="text-xs mt-1 text-blue-700 dark:text-blue-300">
                    {t('limitedLanguagesDesc')}
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Language Selector */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">{t('podcastLanguage')}</Label>
            <div className="flex gap-2 p-1 bg-muted rounded-xl">
              <Button 
                variant={selectedLanguage === 'fr' ? 'default' : 'ghost'} 
                onClick={() => setSelectedLanguage('fr')} 
                className="flex-1 h-12 text-sm rounded-lg font-medium" 
                disabled={isLanguageDisabled('fr')}
              >
                🇫🇷 Français
                {isLanguageDisabled('fr') && <Lock className="w-4 h-4 ml-2" />}
              </Button>
              <Button 
                variant={selectedLanguage === 'en' ? 'default' : 'ghost'} 
                onClick={() => setSelectedLanguage('en')} 
                className="flex-1 h-12 text-sm rounded-lg font-medium" 
                disabled={isLanguageDisabled('en')}
              >
                🇬🇧 English
                {isLanguageDisabled('en') && <Lock className="w-4 h-4 ml-2" />}
              </Button>
            </div>
          </div>


          {/* Loading State */}
          {isGenerating && (
            <Card className="p-6 text-center bg-primary/5 border-primary/20">
              <h4 className="font-semibold mb-3 text-lg">{t('podcastInProgress')}</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t('podcastProgressDesc')}
              </p>
            </Card>
          )}

          {/* Error State */}
          {error && (
            <Card className="p-5 bg-destructive/5 border-destructive/20">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-3 h-3 bg-destructive rounded-full flex-shrink-0" />
                <span className="font-semibold text-destructive">{t('errorTitle')}</span>
              </div>
              <p className="text-sm mb-4 text-destructive leading-relaxed">{error}</p>
              <Button 
                onClick={() => selectedLocation && handleGenerate()} 
                variant="outline" 
                className="w-full h-12 font-medium" 
                disabled={!selectedLocation || isLocationBlocked}
              >
                {t('retry')}
              </Button>
            </Card>
          )}

          {/* Success State */}
          {generatedPodcast && (
            <Card className="p-6 bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800">
              <div className="space-y-5">
                <div className="flex items-center gap-2">
                  <Badge variant="default" className="bg-green-600 text-white px-3 py-1">
                    <Volume2 className="w-4 h-4 mr-2" />
                    {t('podcastReady')}
                  </Badge>
                </div>
                
                {generatedPodcast.Image && (
                  <img 
                    src={generatedPodcast.Image} 
                    alt={generatedPodcast.Name} 
                    className="w-full h-48 object-cover rounded-xl" 
                  />
                )}
                
                <div>
                  <h4 className="font-bold text-xl mb-3">{generatedPodcast.Name}</h4>
                  <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">
                    {currentDescription}
                  </p>
                </div>
                
                {currentAudioFile && (
                  <div className="space-y-3">
                    <Label className="text-base font-semibold">
                      {language === 'FR' ? 'Lecteur audio' : 'Audio player'}
                    </Label>
                    <audio 
                      controls 
                      className="w-full h-12" 
                      src={currentAudioFile} 
                      preload="metadata"
                    >
                      {language === 'FR' ? 'Votre navigateur ne supporte pas l\'élément audio.' : 'Your browser does not support the audio element.'}
                    </audio>
                  </div>
                )}
                
                <Button 
                  onClick={handleClose} 
                  className="w-full btn-adventure h-12 text-base font-semibold"
                >
                  <Play className="w-5 h-5 mr-3" />
                  {t('seeOnMapBtn')}
                </Button>
              </div>
            </Card>
          )}
        </div>
        <DrawerFooter className="p-4 pt-2" style={{ paddingBottom: isKeyboardOpen ? 8 : 'max(16px, env(safe-area-inset-bottom))' }}>
          <Button 
            onClick={handleGenerate} 
            disabled={!selectedLocation || isGenerating || isLocationBlocked} 
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl h-14 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-lg" 
          >
            {isLocationBlocked ? (
              <>
                <Lock className="w-6 h-6 mr-3" />
                {t('placeComplete')}
              </>
            ) : isGenerating ? (
              <>
                <Loader2 className="w-6 h-6 mr-3 animate-spin" />
                {t('generating')}
              </>
            ) : (
              <>
                <Mic className="w-6 h-6 mr-3" />
                {t('generatePodcast')}
              </>
            )}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default MobilePodcastGenerator;